import { useState, FormEvent } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";

type Step = "form" | "verify-pending";
type PasswordStrength = "weak" | "medium" | "strong";

function getPasswordStrength(pw: string): PasswordStrength {
  if (pw.length < 6) return "weak";
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score >= 3) return "strong";
  if (score >= 1) return "medium";
  return "weak";
}

export default function AuthModal() {
  const { isAuthModalOpen, authMode, closeAuthModal, login, register, openAuthModal } = useAuth();
  const { lang } = useLang();

  const [step, setStep] = useState<Step>("form");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [username, setUsername] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const pwStrength = getPasswordStrength(password);

  if (!isAuthModalOpen) return null;

  const resetForm = () => {
    setStep("form");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setUsername("");
    setError("");
    setAgreeTerms(false);
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const handleClose = () => {
    resetForm();
    closeAuthModal();
  };

  const handleSwitchMode = (mode: "login" | "register") => {
    resetForm();
    openAuthModal(mode);
  };

  const handleFormSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    if (authMode === "register") {
      if (!username.trim()) {
        setError(lang === "ja" ? "ユーザー名を入力してください" : "Please enter a username");
        return;
      }
      if (username.length < 3) {
        setError(lang === "ja" ? "ユーザー名は3文字以上にしてください" : "Username must be at least 3 characters");
        return;
      }
      if (!/^[a-zA-Z0-9_]+$/.test(username)) {
        setError(lang === "ja" ? "ユーザー名は英数字とアンダースコアのみ使用可能です" : "Username can only contain letters, numbers, and underscores");
        return;
      }
      if (password.length < 6) {
        setError(lang === "ja" ? "パスワードは6文字以上にしてください" : "Password must be at least 6 characters");
        return;
      }
      if (password !== confirmPassword) {
        setError(lang === "ja" ? "パスワードが一致しません" : "Passwords do not match");
        return;
      }
      if (!agreeTerms) {
        setError(lang === "ja" ? "利用規約に同意してください" : "Please agree to the Terms of Service");
        return;
      }
    }

    setLoading(true);
    try {
      if (authMode === "login") {
        const result = await login(email, password);
        if (!result.success) {
          setError(result.error ?? (lang === "ja" ? "ログインに失敗しました" : "Login failed"));
        } else {
          handleClose();
        }
      } else {
        const result = await register(username, email, password);
        if (!result.success) {
          setError(result.error ?? (lang === "ja" ? "登録に失敗しました" : "Registration failed"));
        } else {
          // メール認証待ち画面へ
          setStep("verify-pending");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const strengthConfig = {
    weak: { label: lang === "ja" ? "弱い" : "Weak", color: "bg-red-500", width: "w-1/3", textColor: "text-red-400" },
    medium: { label: lang === "ja" ? "普通" : "Medium", color: "bg-amber-400", width: "w-2/3", textColor: "text-amber-400" },
    strong: { label: lang === "ja" ? "強い" : "Strong", color: "bg-emerald-400", width: "w-full", textColor: "text-emerald-400" },
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className="absolute inset-0 bg-black/75 backdrop-blur-md"></div>
      <div className="relative bg-[#0c0c14] border border-white/10 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-amber-400 to-transparent"></div>

        {/* Header */}
        <div className="px-7 pt-7 pb-3">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-400/10 rounded-xl border border-amber-400/20">
                <i className="ri-earth-line text-xl text-amber-400"></i>
              </div>
              <div>
                <h2 className="text-white text-lg font-black font-['Space_Grotesk',sans-serif]">Buy the World</h2>
                <p className="text-gray-500 text-xs">
                  {step === "verify-pending"
                    ? (lang === "ja" ? "メール認証待ち" : "Check your email")
                    : authMode === "login"
                      ? (lang === "ja" ? "アカウントにログイン" : "Sign in to your account")
                      : (lang === "ja" ? "新しいアカウントを作成" : "Create a new account")}
                </p>
              </div>
            </div>
            <button onClick={handleClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all cursor-pointer">
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          {authMode === "register" && (
            <div className="flex items-center gap-2 mb-5">
              <div className={`flex items-center gap-1.5 ${step === "form" ? "text-amber-400" : "text-emerald-400"}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border transition-all ${step === "form" ? "bg-amber-400 text-black border-amber-400" : "bg-emerald-400/20 text-emerald-400 border-emerald-400/50"}`}>
                  {step === "form" ? "1" : <i className="ri-check-line text-xs"></i>}
                </div>
                <span className="text-xs font-medium whitespace-nowrap">{lang === "ja" ? "情報入力" : "Info"}</span>
              </div>
              <div className="flex-1 h-px bg-white/10"></div>
              <div className={`flex items-center gap-1.5 ${step === "verify-pending" ? "text-amber-400" : "text-gray-600"}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border transition-all ${step === "verify-pending" ? "bg-amber-400 text-black border-amber-400" : "bg-white/5 text-gray-600 border-white/10"}`}>2</div>
                <span className="text-xs font-medium whitespace-nowrap">{lang === "ja" ? "メール認証" : "Verify"}</span>
              </div>
            </div>
          )}
        </div>

        <div className="px-7 pb-7">

          {/* ===== メール認証待ち画面 ===== */}
          {step === "verify-pending" && (
            <div className="space-y-5">
              <div className="bg-emerald-400/10 border border-emerald-400/20 rounded-xl p-5 text-center">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-3 bg-emerald-400/10 rounded-full border border-emerald-400/20">
                  <i className="ri-mail-check-line text-emerald-400 text-3xl"></i>
                </div>
                <p className="text-emerald-400 text-base font-bold">
                  {lang === "ja" ? "認証メールを送信しました！" : "Verification email sent!"}
                </p>
                <p className="text-gray-400 text-sm mt-1 break-all">{email}</p>
              </div>

              <div className="bg-white/3 border border-white/8 rounded-xl p-4 space-y-3">
                <p className="text-white text-sm font-semibold">{lang === "ja" ? "次のステップ" : "Next steps"}</p>
                <div className="space-y-2 text-xs text-gray-400">
                  <div className="flex items-start gap-2">
                    <span className="text-amber-400 font-bold mt-0.5">1.</span>
                    <span>{lang === "ja" ? "受信トレイを確認してください（迷惑メールも）" : "Check your inbox (and spam folder)"}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-amber-400 font-bold mt-0.5">2.</span>
                    <span>{lang === "ja" ? "「Buy the World」からのメールを開く" : "Open the email from \"Buy the World\""}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-amber-400 font-bold mt-0.5">3.</span>
                    <span>{lang === "ja" ? "メール内の「認証する」ボタンをクリック" : "Click the \"Confirm\" button in the email"}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-amber-400 font-bold mt-0.5">4.</span>
                    <span>{lang === "ja" ? "認証後、ログインしてゲームを開始！" : "After confirming, log in and start playing!"}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 bg-amber-400/10 border border-amber-400/20 rounded-xl px-3 py-2.5">
                <i className="ri-time-line text-amber-400 text-sm flex-shrink-0"></i>
                <p className="text-amber-400 text-xs">{lang === "ja" ? "認証リンクの有効期限は24時間です" : "The confirmation link expires in 24 hours"}</p>
              </div>

              <button
                onClick={() => { handleClose(); openAuthModal("login"); }}
                className="w-full bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-black font-bold rounded-xl py-3.5 text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-login-box-line text-base"></i>
                <span>{lang === "ja" ? "ログイン画面へ" : "Go to Login"}</span>
              </button>
            </div>
          )}

          {/* ===== フォーム ===== */}
          {step === "form" && (
            <form onSubmit={handleFormSubmit} className="space-y-4">
              {authMode === "register" && (
                <div>
                  <label className="block text-gray-400 text-xs mb-1.5 font-medium">
                    {lang === "ja" ? "ユーザー名" : "Username"}<span className="text-red-400 ml-1">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                      <i className="ri-user-line text-gray-500 text-sm"></i>
                    </div>
                    <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="your_username" required
                      className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-amber-400/50 transition-all" />
                  </div>
                  <p className="text-gray-600 text-xs mt-1">{lang === "ja" ? "3文字以上・英数字とアンダースコアのみ" : "3+ chars, letters/numbers/underscore only"}</p>
                </div>
              )}

              <div>
                <label className="block text-gray-400 text-xs mb-1.5 font-medium">
                  {lang === "ja" ? "メールアドレス" : "Email"}<span className="text-red-400 ml-1">*</span>
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                    <i className="ri-mail-line text-gray-500 text-sm"></i>
                  </div>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-amber-400/50 transition-all" />
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-xs mb-1.5 font-medium">
                  {lang === "ja" ? "パスワード" : "Password"}<span className="text-red-400 ml-1">*</span>
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                    <i className="ri-lock-line text-gray-500 text-sm"></i>
                  </div>
                  <input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-10 py-3 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-amber-400/50 transition-all" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-500 hover:text-gray-300 cursor-pointer transition-colors">
                    <i className={showPassword ? "ri-eye-off-line text-sm" : "ri-eye-line text-sm"}></i>
                  </button>
                </div>
                {authMode === "register" && password.length > 0 && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-gray-600 text-xs">{lang === "ja" ? "パスワード強度" : "Strength"}</span>
                      <span className={`text-xs font-medium ${strengthConfig[pwStrength].textColor}`}>{strengthConfig[pwStrength].label}</span>
                    </div>
                    <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-300 ${strengthConfig[pwStrength].color} ${strengthConfig[pwStrength].width}`}></div>
                    </div>
                  </div>
                )}
              </div>

              {authMode === "register" && (
                <div>
                  <label className="block text-gray-400 text-xs mb-1.5 font-medium">
                    {lang === "ja" ? "パスワード（確認）" : "Confirm Password"}<span className="text-red-400 ml-1">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                      <i className="ri-lock-2-line text-gray-500 text-sm"></i>
                    </div>
                    <input type={showConfirmPassword ? "text" : "password"} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="••••••••" required
                      className={`w-full bg-white/5 border rounded-xl pl-10 pr-10 py-3 text-white text-sm placeholder-gray-600 focus:outline-none transition-all ${confirmPassword.length > 0 ? password === confirmPassword ? "border-emerald-400/50" : "border-red-400/50" : "border-white/10 focus:border-amber-400/50"}`} />
                    <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-500 hover:text-gray-300 cursor-pointer transition-colors">
                      <i className={showConfirmPassword ? "ri-eye-off-line text-sm" : "ri-eye-line text-sm"}></i>
                    </button>
                  </div>
                </div>
              )}

              {authMode === "register" && (
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative mt-0.5 flex-shrink-0">
                    <input type="checkbox" checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)} className="sr-only" />
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${agreeTerms ? "bg-amber-400 border-amber-400" : "bg-white/5 border-white/20 group-hover:border-white/40"}`}>
                      {agreeTerms && <i className="ri-check-line text-black text-xs"></i>}
                    </div>
                  </div>
                  <span className="text-gray-400 text-xs leading-relaxed">
                    {lang === "ja" ? (
                      <><a href="#terms" className="text-amber-400 hover:text-amber-300 underline underline-offset-2">利用規約</a>{" と "}<a href="#privacy" className="text-amber-400 hover:text-amber-300 underline underline-offset-2">プライバシーポリシー</a>{" に同意します"}</>
                    ) : (
                      <>{"I agree to the "}<a href="#terms" className="text-amber-400 hover:text-amber-300 underline underline-offset-2">Terms of Service</a>{" and "}<a href="#privacy" className="text-amber-400 hover:text-amber-300 underline underline-offset-2">Privacy Policy</a></>
                    )}
                  </span>
                </label>
              )}

              {error && (
                <div className="flex items-start gap-2 bg-red-400/10 border border-red-400/20 rounded-xl px-3 py-2.5">
                  <i className="ri-error-warning-line text-red-400 text-sm mt-0.5 flex-shrink-0"></i>
                  <p className="text-red-400 text-xs leading-relaxed">{error}</p>
                </div>
              )}

              <button type="submit" disabled={loading || (authMode === "register" && !agreeTerms)}
                className="w-full bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 disabled:opacity-40 disabled:cursor-not-allowed text-black font-bold rounded-xl py-3.5 text-sm transition-all cursor-pointer whitespace-nowrap mt-2 flex items-center justify-center gap-2">
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin"></span>
                    <span>{lang === "ja" ? "処理中..." : "Processing..."}</span>
                  </span>
                ) : authMode === "login" ? (
                  <span className="flex items-center gap-2">
                    <i className="ri-login-box-line text-base"></i>
                    <span>{lang === "ja" ? "ログイン" : "Sign In"}</span>
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <i className="ri-mail-send-line text-base"></i>
                    <span>{lang === "ja" ? "認証メールを送信する" : "Send Verification Email"}</span>
                  </span>
                )}
              </button>
            </form>
          )}

          {step === "form" && (
            <div className="border-t border-white/5 pt-5 mt-5">
              <div className="text-center">
                <span className="text-gray-600 text-xs">
                  {authMode === "login" ? (lang === "ja" ? "アカウントをお持ちでない方は" : "Don't have an account? ") : (lang === "ja" ? "すでにアカウントをお持ちの方は" : "Already have an account? ")}
                </span>
                <button onClick={() => handleSwitchMode(authMode === "login" ? "register" : "login")}
                  className="text-amber-400 hover:text-amber-300 text-xs font-semibold transition-colors cursor-pointer ml-1 whitespace-nowrap">
                  {authMode === "login" ? (lang === "ja" ? "新規登録" : "Register") : (lang === "ja" ? "ログイン" : "Sign In")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
